//@flow
import React, { PureComponent } from 'react';
import * as Immutable from 'immutable';

import PlacesSearchBox from 'common/components/core/map/PlacesSearchBox';
import theme from './ZoneMapPlacesSearchBox.scss';
import { ZoneMapGeometryStatusEnum } from 'common/enums/ZoneMapGeometryStatus';
import { memoizedImmutable } from 'common/utils/Memoizer';
import { ZoneMapGeometryTypeEnum } from 'common/enums/ZoneMapGeometryType';

import { ZoneMapVehicleId } from 'common/models/internal/ZoneMapVehicleId';
import { ZoneMapGeometry } from 'common/models/internal/ZoneMapGeometry';
import { type ZoneMapGeometryModelType } from 'common/models/flow/ZoneMapGeometry';
import { MasterVehicle } from 'common/models/internal/MasterVehicle';
import { ZoneMapFolder } from 'common/models/internal/ZoneMapFolder';
import { ExtendedUser } from 'common/models/internal/ExtendedUser';

const FILTERABLE_FIELDS: Array<$Keys<ZoneMapGeometryModelType>> = [
    'name',
    'routeId',
    'description'
];

type CachedListType = Array<{
    name: string,
    address: string,
    target: ZoneMapGeometry,
    type: string
}>;

type Props = {
    availableRiders: Immutable.List<ExtendedUser>,
    availableVehicles: Immutable.List<MasterVehicle>,
    title: string,
    subtitle: string,
    onChange: () => void,
    placeholder: string,
    geometries: Immutable.List<ZoneMapGeometry>,
    folders: Immutable.List<ZoneMapFolder>,
    getRadiusAndCenter: () => void,
    onBlur: () => void,
    onFocus: () => void
};

class ZoneMapPlacesSearchBox extends PureComponent<Props> {
    @memoizedImmutable
    getZoneList(geometries, folders) {
        let cachedSearchList = [];
        if (geometries) {
            cachedSearchList = geometries.reduce((zoneList, geometry) => {
                const { name, routeId } = geometry;
                const status = geometry.getInternalStatus();
                const type = geometry.getType();
                let updatedZoneList = zoneList;
                if (
                    status !== ZoneMapGeometryStatusEnum.Inactive &&
                    status !== ZoneMapGeometryStatusEnum.Search &&
                    type !== ZoneMapGeometryTypeEnum.Point
                ) {
                    updatedZoneList = zoneList.concat({
                        name,
                        address: routeId || '',
                        target: geometry,
                        type: 'geometry'
                    });
                }
                return updatedZoneList;
            }, cachedSearchList);
        }

        if (folders) {
            cachedSearchList = folders.reduce((searchList, folder) => {
                const { name } = folder;
                return searchList.concat({
                    name,
                    target: folder,
                    type: 'folder'
                });
            }, cachedSearchList);
        }
        return cachedSearchList;
    }

    filterListFields = (fieldValue = [], searchValue) => {
        return fieldValue.reduce(
            (acc, value) =>
                acc || value.toUpperCase().includes(searchValue.toUpperCase()),
            false
        );
    };

    filterUserField = (users = [], searchValue = '') => {
        const { availableRiders } = this.props;

        return users.reduce((acc, { userId }) => {
            let isFound = false;
            const matchedRider = availableRiders.find(
                rider => rider.userId === userId
            );

            if (matchedRider) {
                isFound =
                    matchedRider.name
                        .toUpperCase()
                        .includes(searchValue.toUpperCase()) ||
                    matchedRider.userId
                        .toUpperCase()
                        .includes(searchValue.toUpperCase());
            } else {
                isFound = userId
                    .toUpperCase()
                    .includes(searchValue.toUpperCase());
            }

            return acc || isFound;
        }, false);
    };

    filterVehicleField = (
        vehicles: Immutable.List<ZoneMapVehicleId> = Immutable.List(),
        searchValue: string = ''
    ): boolean => {
        const { availableVehicles } = this.props;
        return vehicles.some(({ id }) => {
            const matchedVehicle = availableVehicles.find(
                vehicle => vehicle.id === id
            );
            return matchedVehicle
                ? matchedVehicle.name
                      .toUpperCase()
                      .includes(searchValue.toUpperCase()) ||
                      matchedVehicle.id
                          .toUpperCase()
                          .includes(searchValue.toUpperCase())
                : id.toUpperCase().includes(searchValue.toUpperCase());
        });
    };

    filterCachedSearchList = (
        cachedList: CachedListType,
        searchValue: string = ''
    ): CachedListType => {
        return cachedList.filter(cachedValue => {
            const { target } = cachedValue;
            let doFilter = FILTERABLE_FIELDS.reduce((acc, field) => {
                const value = target.get(field);
                return (
                    acc ||
                    (value &&
                        value.toUpperCase().includes(searchValue.toUpperCase()))
                );
            }, false);

            if (!doFilter) {
                const [isPincodeHit, isPlacesHit, isUsersHit, isVehiclesHit] = [
                    this.filterListFields(target.pincodes, searchValue),
                    this.filterListFields(target.localities, searchValue),
                    this.filterUserField(target.users, searchValue),
                    this.filterVehicleField(target.vehicles, searchValue)
                ];
                doFilter =
                    isPincodeHit || isPlacesHit || isUsersHit || isVehiclesHit;
            }

            return doFilter;
        });
    };

    noOp = () => {};

    render() {
        const {
            title,
            subtitle,
            onChange,
            placeholder,
            geometries,
            folders,
            getRadiusAndCenter,
            onBlur,
            onFocus
        } = this.props;
        return (
            <PlacesSearchBox
                name={title}
                getRadiusAndCenter={getRadiusAndCenter}
                selectedAddress={subtitle}
                onPlacesChanged={onChange}
                theme={theme}
                placeholder={placeholder}
                autoFocus={false}
                cachedSearchList={this.getZoneList(geometries, folders)}
                icon="search"
                resultIcons={{ geometry: 'zone', folder: 'folder-close' }}
                onPlacesClear={this.noOp}
                onBlur={onBlur}
                onFocus={onFocus}
                filterCachedSearchList={this.filterCachedSearchList}
                portal
            />
        );
    }
}

export default ZoneMapPlacesSearchBox;
